import React from "react";
import PropTypes from "prop-types";

import IconContext from '../context/IconContext';

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconCancel: React.ComponentType<SvgComponentProps>;
    IconEllipsis: React.ComponentType<SvgComponentProps>;

    // To use custom SVGs, update the config to define these keys.
    IconContextMenuCancel: React.ComponentType<SvgComponentProps>;
    IconContextMenuEllipsis: React.ComponentType<SvgComponentProps>;
}

// TODO: Implement icon. Should be simple, but is missing a CSS class to format the icon correctly.
interface ContextMenuProps {
    isHidden?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Function
    onClick?: () => void;
    onBlur?: () => void;
}

/**
 * A context menu is a list of options that pops up when 
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isHidden - Controls the visibility of the context menu. Defaults to true. 
 * @param {React.ReactNode} children - The contents inside the context menu. These should be of type `ContextMenuItem`.
 * @param {string} className - CSS classes to apply to the context menu.
 * 
 * ## _
 * ### Functions
 * @param {function} onClick - Function that is called when the button is clicked. This generally should be filled in with something flipping the `isHidden` field.
 * @param {function} onBlur - Function that is called when the component is blurred. This generally should be filled in with something setting the `isHidden` field to false.
 * 
 * ## _
 * ### Icons
 * Declare these in your application's icon configuration.
 * @inner `IconContextMenuCancel` - An optional icon override for the cancel button.
 * @inner `IconContextMenuEllipsis` - An optional icon override for the ellipsis button.
 */
class ContextMenu extends React.Component<ContextMenuProps> {
    static contextType = IconContext;
    context!: IIconContext;
    
    static propTypes = {
        isHidden: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
        onClick: PropTypes.func,
        onBlur: PropTypes.func,
    };

    static defaultProps = {
        isHidden: true,
        className: "",
    };

    render() {
        const { isHidden, children, className, onClick, onBlur, ...rest } = this.props;
        const { 
            IconCancel, IconEllipsis,
            IconContextMenuCancel, IconContextMenuEllipsis,
        } = this.context;

        const CancelIcon = IconContextMenuCancel || IconCancel;
        const EllipsisIcon = IconContextMenuEllipsis || IconEllipsis;

        return (
            <div 
                className={`o-button-context-menu ${!isHidden && "o-button-context-menu--shown"} ${className}`}
                onBlur={(e) => {
                    e.preventDefault();
                    if (!e.currentTarget.contains(e.relatedTarget) && onBlur) {
                        onBlur();
                    }
                }}
            >
                <button 
                    className="a-icon-link o-button-context-menu__toggle-button" 
                    onClick={onClick}
                >
                    <div className="a-icon-link__inner">
                        <div className="a-icon-link__icon-wrapper">
                            { isHidden ? <EllipsisIcon className="a-icon-link__icon" /> : <CancelIcon className="a-icon-link__icon" /> }
                        </div>
                    </div>
                </button>
                <ul className="m-context-menu o-button-context-menu__menu">
                    {children}
                </ul>
            </div>
        );
    }
}

export default ContextMenu;